import type { CreateIngredientRequest } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button, InputGroupInput, ProgressButton, Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui'
import { useWebSocketContext } from "@/contexts"
import { useKeydownShortcut } from '@/hooks'
import { DEFAULT_CATEGORY_ORDER } from '@/lib/ingredientCategories'

const INGREDIENT_UNITS = [
  { value: 'bag', name: 'Bag' },
  { value: 'bunch', name: 'Bunch' },
  { value: 'head', name: 'Head' },
  { value: 'loaf', name: 'Loaf' },
  { value: 'package', name: 'Package' },
  { value: 'piece', name: 'Piece' },
  { value: 'ml', name: 'Milliliter (ml)' },
  { value: 'l', name: 'Liter (l)' },
  { value: 'tsp', name: 'Teaspoon (tsp)' },
  { value: 'tbsp', name: 'Tablespoon (tbsp)' },
  { value: 'c', name: 'Cup (c)' },
  { value: 'fl-oz', name: 'Fluid Ounce (fl-oz)' },
  { value: 'pt', name: 'Pint (pt)' },
  { value: 'qt', name: 'Quart (qt)' },
  { value: 'gal', name: 'Gallon (gal)' },
  { value: 'mg', name: 'Milligram (mg)' },
  { value: 'g', name: 'Gram (g)' },
  { value: 'kg', name: 'Kilogram (kg)' },
  { value: 'oz', name: 'Ounce (oz)' },
  { value: 'lb', name: 'Pound (lb)' },
  // { value: 'cm', name: 'Centimeter (cm)' },
  // { value: 'in', name: 'Inch (in)' },
] as const

export interface IngredientDialogProps {
  open: boolean
  onClose: () => void
}

export default function IngredientDialog({
  open,
  onClose,
}: IngredientDialogProps) {
  const [name, setName] = useState<string>("")
  const [quantity, setQuantity] = useState<string>("")
  const [unit, setUnit] = useState<string | null>("")
  const [category, setCategory] = useState<string | null>("")
  const [isLoading, _setIsLoading] = useState(false)
  const [shouldClose, _setShouldClose] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const { createIngredient } = useWebSocketContext()
  const { mixId = '' } = useParams()

  useKeydownShortcut(
    { key: 'Escape', enabled: open },
    () => {
      if (open) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }
    },
    'Close Dialog',
    'Close the alacart ingredient dialog'
  );

  useEffect(() => {
    if (!open) return

    if (shouldClose) {
      setName("")
      setQuantity("")
      setUnit(null)
      setCategory(null)
    }
  }, [shouldClose]);

  const handleCancel = () => {
    onClose()
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      (e.target as HTMLInputElement).blur()
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Create a new ingredient with a unique ID
    const request: CreateIngredientRequest = {
      category: category,
      name: name,
      quantity: quantity,
      unit: unit,
    };

    createIngredient(mixId, request)

    // Reset form fields
    setName("");
    setQuantity("");
    setUnit("");
    setCategory("");

    // Close the dialog
    onClose();
  }

  if (!open || shouldClose) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="flex max-w-md w-full mx-6">
        <div
          className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md border border-border"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="select-none text-2xl font-bold text-foreground">
              Add Ingredient
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">
                  Name
                </label>
                <InputGroupInput
                  autoFocus={true}
                  data-1p-ignore
                  disabled={isLoading}
                  onChange={(e) => setName(e.target.value)}
                  onKeyUp={handleKeyUp}
                  placeholder="Ingredient name"
                  ref={nameInputRef}
                  type="text"
                  value={name}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-foreground">
                    Quantity
                  </label>
                  <InputGroupInput
                    disabled={isLoading}
                    onChange={(e) => setQuantity(e.target.value)}
                    onKeyUp={handleKeyUp}
                    placeholder="Quantity"
                    type="text"
                    value={quantity}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-foreground">
                    Unit
                  </label>
                  <Select value={unit || ""} onValueChange={setUnit} disabled={isLoading}>
                    <SelectTrigger className="w-full text-foreground [&>span]:text-foreground">
                      <SelectValue placeholder="Select unit" className="text-foreground" />
                    </SelectTrigger>
                    <SelectContent>
                      {INGREDIENT_UNITS.map((unitItem) => (
                        <SelectItem key={unitItem.value} value={unitItem.value}>
                          {unitItem.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">
                  Category
                </label>
                <Select value={category || ""} onValueChange={setCategory} disabled={isLoading}>
                  <SelectTrigger className="w-full text-foreground [&>span]:text-foreground">
                    <SelectValue placeholder="Select category" className="text-foreground" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...DEFAULT_CATEGORY_ORDER].sort((a, b) => a.localeCompare(b)).map((categoryItem) => (
                      <SelectItem key={categoryItem} value={categoryItem}>
                        {categoryItem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 flex w-full gap-2">
                <Button
                  className="flex-1 text-foreground"
                  disabled={isLoading}
                  onClick={handleCancel}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <ProgressButton className="flex-1" isLoading={isLoading}>
                  <span>Submit</span>
                </ProgressButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
